/**
 * Predictive Image Preloader
 *
 * Watches `img[loading="lazy"]` elements and switches them to `eager`
 * when they enter a configurable preload zone ahead of the viewport.
 * This gives control over when images start fetching — independent of
 * the browser's built-in lazy threshold (~1250px in Chrome, varies).
 *
 * Carousel-aware: only preloads images in slides currently visible in
 * the carousel viewport (respects slides-per-view). Off-screen carousel
 * slides and clones are skipped — the carousel's own #preloadAdjacentSlides
 * handles those on swipe.
 *
 * Idle prefetch: images marked `data-preload="idle"` are fetched once the
 * page has fully loaded and the browser is idle — regardless of viewport
 * distance — so they're already cached when the user scrolls to them.
 *
 * Execution is deferred via requestIdleCallback to avoid impacting LCP.
 */

import { eventBus } from '@theme/events';

const debug = (...args) => { if (window.__THEME_DEBUG_EVENTS__) console.log('[ImagePreloader]', ...args); };

/** How far ahead of the viewport to start loading (100 = 1 viewport).
 *  Touch devices flick several viewports per second — deeper zone so image
 *  fetches win the race against the scroll. */
const PRELOAD_VH = (window.matchMedia?.('(hover: none)').matches ?? false) ? 350 : 200;

const preloadPx = Math.round((PRELOAD_VH / 100) * window.innerHeight);

const observer = new IntersectionObserver((entries) => {
	for (const entry of entries) {
		if (entry.isIntersecting) {
			const img = entry.target;
			img.loading = 'eager';
			observer.unobserve(img);
			debug('→ eager:', img.src.split('/').pop()?.split('?')[0]);
		}
	}
}, {
	// Symmetric margin: scrolling back UP must also find images ready — a
	// rehydrated band can reinsert imgs that never got flipped to eager.
	rootMargin: `${preloadPx}px 0px ${preloadPx}px 0px`,
	threshold: 0,
});

/**
 * Scan for lazy images and observe them.
 * Carousel-aware: skips clones and off-screen carousel slides.
 * @param {Document|HTMLElement} root
 * @param {string} [source='init'] - Label for debug logging
 */
function observeImages(root = document, source = 'init') {
	let observed = 0;
	let skippedClone = 0;
	let skippedOffscreen = 0;
	let alreadyEager = 0;

	for (const img of root.querySelectorAll('img[loading="lazy"], img[loading="auto"], img:not([loading])')) {
		if (img.loading === 'eager') continue;
		const slide = img.closest('.carousel__slide');
		if (slide) {
			if (slide.hasAttribute('data-clone')) { skippedClone++; continue; }
			const viewport = slide.closest('.carousel__viewport');
			if (viewport && slide.offsetLeft >= viewport.clientWidth) { skippedOffscreen++; continue; }
		}
		observer.observe(img);
		observed++;
	}

	alreadyEager = root.querySelectorAll('img[loading="eager"]').length;

	debug(`scan (${source}):`, {
		observed,
		skippedClone,
		skippedOffscreen,
		alreadyEager,
		preloadZone: `${preloadPx}px (${PRELOAD_VH}vh)`,
	});
}

/**
 * Fade-in for late arrivals inside product grids: an image that finishes
 * loading while already in the viewport fades in over the LQIP/placeholder
 * instead of popping — off-screen loads clear before they're seen, so the
 * content reads as "always there". data-pending is cleared by the capture
 * listeners below (load/error don't bubble). CSS lives in the grid blocks:
 * [data-product-grid] img[data-pending] { opacity: 0 }.
 */
function markPendingImages(root = document) {
	for (const img of root.querySelectorAll('[data-product-grid] img')) {
		if (img.complete) {
			img.removeAttribute('data-pending');
		} else {
			img.setAttribute('data-pending', '');
		}
	}
}

for (const type of ['load', 'error']) {
	document.addEventListener(type, (e) => {
		if (e.target instanceof HTMLImageElement) e.target.removeAttribute('data-pending');
	}, true);
}

// Defer initial scan until browser is idle — never block LCP
function whenIdle(fn, timeout) {
	'requestIdleCallback' in window ? requestIdleCallback(fn, timeout ? { timeout } : undefined) : setTimeout(fn, 200);
}

whenIdle(() => {
	observeImages(document, 'initial');
	markPendingImages();
});

/**
 * Prefetch images opted into idle preloading (`data-preload="idle"`).
 * Runs after the `load` event, so it never competes with the LCP image
 * or initial metrics — by the time the user scrolls there, it's cached.
 */
function prefetchIdleImages() {
	for (const img of document.querySelectorAll('img[data-preload="idle"]')) {
		if (img.loading !== 'lazy') continue;
		observer.unobserve(img);
		img.setAttribute('fetchpriority', 'low');
		img.loading = 'eager';
		debug('idle prefetch:', img.src.split('/').pop()?.split('?')[0]);
	}
}

const startIdlePrefetch = () => whenIdle(prefetchIdleImages, 3000);
if (document.readyState === 'complete') {
	startIdlePrefetch();
} else {
	window.addEventListener('load', startIdlePrefetch, { once: true });
}

// Re-scan when infinite scroll appends products
eventBus.on('collection:updated', () => {
	observeImages(document, 'collection:updated');
	markPendingImages();
	whenIdle(prefetchIdleImages);
});

debug('registered', { preloadZone: `${preloadPx}px (${PRELOAD_VH}vh)` });
